import { useEffect, useLayoutEffect, useState } from 'react';
import { PhoneFrame } from './PhoneFrame';
import './EmotionalMomentsDemo.css';

const HOT_TEXT =
  "You're late AGAIN. This is becoming a pattern and I'm sick of it.";
const CALM_TEXT =
  "Traffic was rough today. Can we add a 15-minute buffer to exchange times so the kids aren't waiting?";

const HOT_WORDS = HOT_TEXT.split(' ');
const CALM_WORDS = CALM_TEXT.split(' ');

const SCENE_MS = [7000, 6000, 5000] as const;

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

function RewriteIcon() {
  return (
    <svg className='em-rewrite__icon' viewBox='0 0 16 16' aria-hidden='true'>
      <path
        d='M8 1.8l1.1 3.1L12.2 6l-3.1 1.1L8 10.2 6.9 7.1 3.8 6l3.1-1.1L8 1.8z'
        fill='currentColor'
      />
      <path
        d='M12.4 9.6l.6 1.6 1.6.6-1.6.6-.6 1.6-.6-1.6-1.6-.6 1.6-.6.6-1.6z'
        fill='currentColor'
        opacity='.7'
      />
    </svg>
  );
}

function MessagesHeader() {
  return (
    <header className='em-head'>
      <div>
        <p className='em-head__eyebrow'>Messages</p>
        <p className='em-head__title'>Draft to Jordan</p>
      </div>
      <span className='em-head__avatar' aria-hidden='true'>
        J
      </span>
    </header>
  );
}

function TemperatureVignette({
  active,
  reduced,
}: {
  active: boolean;
  reduced: boolean;
}) {
  const [stage, setStage] = useState(reduced ? 3 : 0);
  const [typed, setTyped] = useState(reduced ? HOT_WORDS.length : 0);

  useLayoutEffect(() => {
    if (reduced) {
      setStage(3);
      setTyped(HOT_WORDS.length);
      return;
    }

    if (!active) {
      const reset = window.setTimeout(() => {
        setStage(0);
        setTyped(0);
      }, 700);
      return () => window.clearTimeout(reset);
    }

    let revealed = 0;
    let typing: number | undefined;

    const typeStart = window.setTimeout(() => {
      typing = window.setInterval(() => {
        revealed += 1;
        setTyped(revealed);
        if (revealed >= HOT_WORDS.length && typing !== undefined) {
          window.clearInterval(typing);
        }
      }, 105);
    }, 320);

    const chip = window.setTimeout(() => setStage(1), 1950);
    const rewrite = window.setTimeout(() => setStage(2), 2700);
    const calm = window.setTimeout(() => setStage(3), 4750);

    return () => {
      window.clearTimeout(typeStart);
      if (typing !== undefined) window.clearInterval(typing);
      window.clearTimeout(chip);
      window.clearTimeout(rewrite);
      window.clearTimeout(calm);
    };
  }, [active, reduced]);

  const leaving = stage >= 2;
  const chipReady = stage >= 1;
  const calmReady = stage >= 2;

  return (
    <div className='em-a'>
      <MessagesHeader />

      <div className='em-a__stage'>
        <div className='em-incoming'>
          <span className='em-incoming__name'>Jordan</span>
          <p>The school called again about pickup.</p>
        </div>

        <span className={`em-draft-label${chipReady ? ' is-on' : ''}`}>
          Draft &middot; only you
        </span>

        <div
          className={[
            'em-bubble',
            stage >= 3 ? 'is-calm' : '',
            !reduced && stage >= 3 ? 'is-glowing' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div className='em-bubble__text'>
            {!reduced && (
              <p className={`em-line em-line--hot${leaving ? ' is-leaving' : ''}`}>
                {HOT_WORDS.map((word, index) => (
                  <span
                    key={`${word}-${index}`}
                    className={`em-word${index < typed ? ' is-typed' : ''}`}
                    style={leaving ? { animationDelay: `${index * 65}ms` } : undefined}
                  >
                    {word}
                  </span>
                ))}
              </p>
            )}

            {calmReady && (
              <p
                className={`em-line em-line--calm${
                  reduced ? ' is-static' : ' is-entering'
                }`}
              >
                {CALM_WORDS.map((word, index) => (
                  <span
                    key={`${word}-${index}`}
                    className='em-word'
                    style={
                      reduced
                        ? undefined
                        : { animationDelay: `${140 + index * 85}ms` }
                    }
                  >
                    {word}
                  </span>
                ))}
              </p>
            )}
          </div>
        </div>

        <span
          className={[
            'em-chip',
            stage >= 3 ? 'em-chip--calm' : 'em-chip--hot',
            chipReady ? 'is-on' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <span className='em-chip__dot' aria-hidden='true' />
          <span>
            Temperature Check &middot;{' '}
            <span className='em-chip__swap'>
              <span className={stage >= 3 ? 'is-hidden' : ''}>Heated</span>
              <span className={stage >= 3 ? '' : 'is-hidden'}>Calm</span>
            </span>
          </span>
        </span>

        {!reduced && (
          <span
            className={[
              'em-rewrite',
              chipReady ? 'is-on' : '',
              stage >= 2 ? 'is-pressing' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <RewriteIcon />
            Rewrite
          </span>
        )}
      </div>

      <p className={`em-a__note${stage >= 3 ? ' is-on' : ''}`}>
        Harbor keeps the message, softens the delivery.
      </p>
    </div>
  );
}

function ReceiptVignette({ active }: { active: boolean }) {
  const [stage, setStage] = useState(0);
  const [balance, setBalance] = useState(0);

  useLayoutEffect(() => {
    if (!active) {
      const reset = window.setTimeout(() => {
        setStage(0);
        setBalance(0);
      }, 700);
      return () => window.clearTimeout(reset);
    }

    const flash = window.setTimeout(() => setStage(1), 1000);
    const scan = window.setTimeout(() => setStage(2), 1220);
    const logged = window.setTimeout(() => setStage(3), 2400);

    return () => {
      window.clearTimeout(flash);
      window.clearTimeout(scan);
      window.clearTimeout(logged);
    };
  }, [active]);

  useEffect(() => {
    if (stage < 3) return;

    let frame = 0;
    let start = 0;
    const kickoff = window.setTimeout(() => {
      const step = (now: number) => {
        if (!start) start = now;
        const progress = Math.min(1, (now - start) / 1000);
        const eased = 1 - Math.pow(1 - progress, 3);
        setBalance(21 * eased);
        if (progress < 1) frame = window.requestAnimationFrame(step);
      };
      frame = window.requestAnimationFrame(step);
    }, 450);

    return () => {
      window.clearTimeout(kickoff);
      window.cancelAnimationFrame(frame);
    };
  }, [stage]);

  const logged = stage >= 3;

  return (
    <div className='em-b'>
      <div className={`em-camera${logged ? ' is-gone' : ''}`}>
        <div className='em-cam__top'>
          <span className='em-cam__back' aria-hidden='true'>
            &larr;
          </span>
          <span>Scan receipt</span>
          <span className='em-cam__cancel'>Cancel</span>
        </div>

        <div className='em-cam__frame'>
          <span className='em-bracket em-bracket--tl' />
          <span className='em-bracket em-bracket--tr' />
          <span className='em-bracket em-bracket--bl' />
          <span className='em-bracket em-bracket--br' />

          <div className={`em-receipt${stage >= 2 ? ' is-scanning' : ''}`}>
            <div className='em-receipt__head'>
              <span>HARBOR MARKET</span>
              <span>06 / 14</span>
            </div>
            <div className='em-receipt__lines' aria-hidden='true'>
              <span style={{ width: '86%' }} />
              <span style={{ width: '64%' }} />
              <span style={{ width: '92%' }} />
              <span style={{ width: '48%' }} />
            </div>
            <div className='em-receipt__total'>
              <span>FIELD TRIP</span>
              <span>$42.00</span>
            </div>
            <div className='em-receipt__lines em-receipt__lines--faint' aria-hidden='true'>
              <span style={{ width: '70%' }} />
              <span style={{ width: '54%' }} />
            </div>
            <span className='em-receipt__scan' aria-hidden='true' />
          </div>
        </div>

        <div className='em-cam__hint'>Align the receipt inside the frame</div>
        <span className={`em-shutter${stage >= 1 ? ' is-pressed' : ''}`} aria-hidden='true' />
        <span
          className={`em-cam__flash${stage >= 1 ? ' is-flashing' : ''}`}
          aria-hidden='true'
        />
      </div>

      <div className={`em-expense${logged ? ' is-on' : ''}`}>
        <header className='em-head'>
          <div>
            <p className='em-head__eyebrow'>Expenses</p>
            <p className='em-head__title'>Camp &middot; Field trip</p>
          </div>
          <span className='em-head__pill'>Auto-captured</span>
        </header>

        <div className='em-expense__card'>
          <div className='em-expense__row'>
            <span className='em-expense__icon' aria-hidden='true'>
              <svg viewBox='0 0 20 20'>
                <path
                  d='M5 2.5h10v15l-2-1.4-2 1.4-2-1.4-2 1.4-2-1.4V2.5z'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='1.4'
                  strokeLinejoin='round'
                />
                <path
                  d='M7.4 6.6h5.2M7.4 9.4h5.2M7.4 12.2h3.2'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='1.3'
                  strokeLinecap='round'
                />
              </svg>
            </span>
            <span className='em-expense__meta'>
              <span className='em-expense__name'>Field trip</span>
              <span className='em-expense__sub'>Camp &middot; Jun 14</span>
            </span>
            <span className='em-expense__amount'>$42.00</span>
          </div>

          <span className='em-split'>
            <svg viewBox='0 0 14 14' aria-hidden='true'>
              <circle cx='7' cy='7' r='5.4' fill='none' stroke='currentColor' strokeWidth='1.2' />
              <path d='M7 1.6v10.8' stroke='currentColor' strokeWidth='1.2' />
            </svg>
            50/50 &middot; $21.00 each
          </span>
        </div>

        <div className='em-expense__spacer' />

        <div className='em-balance'>
          <span className='em-balance__label'>
            Your share
            <span className='em-balance__sub'>Split with Jordan</span>
          </span>
          <span className='em-balance__amount'>${balance.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}

function ExportVignette({ active }: { active: boolean }) {
  const [stage, setStage] = useState(0);

  useLayoutEffect(() => {
    if (!active) {
      const reset = window.setTimeout(() => setStage(0), 700);
      return () => window.clearTimeout(reset);
    }

    const press = window.setTimeout(() => setStage(1), 350);
    const progress = window.setTimeout(() => setStage(2), 720);
    const preview = window.setTimeout(() => setStage(3), 2100);

    return () => {
      window.clearTimeout(press);
      window.clearTimeout(progress);
      window.clearTimeout(preview);
    };
  }, [active]);

  const exported = stage >= 3;

  return (
    <div className='em-c'>
      <header className='em-head'>
        <div>
          <p className='em-head__eyebrow'>Records</p>
          <p className='em-head__title'>Court-ready history</p>
        </div>
        <span className='em-head__pill'>214 pages</span>
      </header>

      <div className='em-export__card'>
        <span className='em-export__icon' aria-hidden='true'>
          <svg viewBox='0 0 24 24'>
            <path
              d='M7 2.8h7.4L19 7.4v13.8H7z'
              fill='none'
              stroke='currentColor'
              strokeWidth='1.4'
              strokeLinejoin='round'
            />
            <path
              d='M14.2 2.8v4.8H19M9.8 12h6.4M9.8 15.2h6.4M9.8 18.2h4'
              fill='none'
              stroke='currentColor'
              strokeWidth='1.4'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </span>
        <p className='em-export__title'>Export your records</p>
        <p className='em-export__copy'>
          Every message, timestamped and unaltered. Prepared in seconds.
        </p>

        <span
          className={`em-export__button${stage >= 1 ? ' is-pressing' : ''}`}
        >
          Export records
          <svg viewBox='0 0 14 14' aria-hidden='true'>
            <path
              d='M7 2.4v7.2M4.2 6.9L7 9.7l2.8-2.8M2.6 11.4h8.8'
              fill='none'
              stroke='currentColor'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </span>

        <span className='em-progress' aria-hidden='true'>
          <span className={`em-progress__fill${stage >= 2 ? ' is-on' : ''}`} />
        </span>
      </div>

      <div className='em-export__preview'>
        <p className='em-export__preview-label'>Includes</p>
        <ul className='em-export__list'>
          <li>Every message, in order</li>
          <li>Timestamps and edit history</li>
          <li>Expenses and signed agreements</li>
        </ul>
      </div>

      <div className={`em-doc${exported ? ' is-up' : ''}`}>
        <span className='em-doc__grabber' aria-hidden='true' />
        <div className='em-doc__top'>
          <div>
            <p className='em-doc__brand'>
              <span className='em-doc__mark' aria-hidden='true' />
              Harbor Records
            </p>
            <p className='em-doc__title'>Harbor Records Export</p>
            <p className='em-doc__sub'>
              Court-ready &middot; Every message timestamped &middot; 214 pages
            </p>
          </div>
          <svg className='em-check' viewBox='0 0 48 48' aria-hidden='true'>
            <circle cx='24' cy='24' r='21' className='em-check__ring' />
            <path
              d='M15 24.5l6.5 6.5L33.5 18'
              className='em-check__mark'
            />
          </svg>
        </div>

        <div className='em-doc__meta'>
          <span>Generated Jun 14, 2026</span>
          <span>Verified record</span>
        </div>

        <div className='em-doc__page' aria-hidden='true'>
          <span className='em-doc__line em-doc__line--strong' />
          <span className='em-doc__line' style={{ width: '88%' }} />
          <span className='em-doc__line' style={{ width: '94%' }} />
          <span className='em-doc__line' style={{ width: '71%' }} />
          <span className='em-doc__line em-doc__line--gap' style={{ width: '82%' }} />
          <span className='em-doc__line' style={{ width: '90%' }} />
          <span className='em-doc__line' style={{ width: '58%' }} />
        </div>

        <p className='em-doc__foot'>Exported from Harbor &middot; Unaltered</p>
      </div>
    </div>
  );
}

export function EmotionalMomentsDemo() {
  const reduced = usePrefersReducedMotion();
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (reduced) return;
    const timer = window.setTimeout(
      () => setActive((current) => (current + 1) % SCENE_MS.length),
      SCENE_MS[active],
    );
    return () => window.clearTimeout(timer);
  }, [active, reduced]);

  return (
    <div className='em-page'>
      <p className='em-hypothesis'>
        <span className='em-hypothesis__label'>What this tests</span> the
        product&rsquo;s emotional payoffs &mdash; calm tone, effortless expense
        capture, court-ready records &mdash; shown as motion, not claims.
      </p>

      <PhoneFrame size='lg' float label='Emotional Moments'>
        <div className='em-demo'>
          {reduced ? (
            <section className='em-scene is-active'>
              <TemperatureVignette active reduced />
            </section>
          ) : (
            <>
              <section className={`em-scene${active === 0 ? ' is-active' : ''}`}>
                <TemperatureVignette active={active === 0} reduced={false} />
              </section>
              <section className={`em-scene${active === 1 ? ' is-active' : ''}`}>
                <ReceiptVignette active={active === 1} />
              </section>
              <section className={`em-scene${active === 2 ? ' is-active' : ''}`}>
                <ExportVignette active={active === 2} />
              </section>
            </>
          )}

          <div className='em-dots' aria-hidden='true'>
            {[0, 1, 2].map((index) => (
              <span
                key={index}
                className={`em-dot${
                  (reduced ? 0 : active) === index ? ' is-active' : ''
                }`}
              />
            ))}
          </div>
        </div>
      </PhoneFrame>
    </div>
  );
}
